/**
 * Consecutive read-only calls folded into one line, Cursor's "Explored 3
 * files": open while the agent is still reading (each row appears as it
 * happens), folded to the summary once it moves on. Writes, background runs
 * and anything that needs a person never fold.
 */
import { ChevronRight, FolderSearch, Loader2 } from "lucide-react";
import { memo, useEffect, useMemo, useState, type FC } from "react";

import { Shimmer } from "@/components/ai-elements/shimmer";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

import type { ConnectAppsOutput } from "@/features/connectors/types";
import type { ApprovalDecision } from "@/features/agents/types";

import type { AskUserAnswers } from "../../types";
import { ToolCard, toolNameOf, type AnyToolPart } from "./ToolCard";
import { rowStatusOf, useArtifactResolver } from "./ToolRow";
import { describeTool, foldTitle } from "./toolTitles";

type Props = {
  parts: readonly AnyToolPart[];
  /** The fold the agent is still working in stays open. */
  live: boolean;
  onApproval: (decision: ApprovalDecision) => void;
  onAnswer?: (toolCallId: string, answers: AskUserAnswers) => void;
  onConnectResolved?: (toolCallId: string, output: ConnectAppsOutput) => void;
};

export const ExploredFold: FC<Props> = memo(
  ({ parts, live, onApproval, onAnswer, onConnectResolved }) => {
    const resolve = useArtifactResolver();
    const running = parts.some((p) => rowStatusOf(p) === "running");
    const titles = useMemo(
      () => parts.map((p) => describeTool(toolNameOf(p), p.input, resolve)),
      [parts, resolve]
    );
    const title = foldTitle(titles, running);
    // Open while live; fold once the agent has moved on. A person who opened
    // it by hand keeps it open.
    const [open, setOpen] = useState(live);
    const [touched, setTouched] = useState(false);
    useEffect(() => {
      if (!touched) setOpen(live);
    }, [live, touched]);
    return (
      <Collapsible
        open={open}
        onOpenChange={(next) => {
          setTouched(true);
          setOpen(next);
        }}
        className="group/fold min-w-0"
        data-testid="explored-fold"
        data-running={running ? "true" : undefined}
        data-count={parts.length}
      >
        <CollapsibleTrigger
          className="-ml-1 flex h-6 w-[calc(100%+0.25rem)] min-w-0 items-center gap-1.5 rounded-sm px-1 text-left text-base text-fg-3 hover:bg-fill-1 hover:text-fg"
          data-testid="explored-fold-trigger"
        >
          <span className="flex size-3.5 shrink-0 items-center justify-center">
            {running ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <FolderSearch className="size-3.5 text-fg-3" />
            )}
          </span>
          <span className="min-w-0 flex-1 truncate">
            {running ? (
              <Shimmer as="span" className="text-base" duration={1.6}>
                {title}
              </Shimmer>
            ) : (
              title
            )}
          </span>
          <ChevronRight
            className={cn(
              "size-3 shrink-0 text-fg-4 opacity-0 transition-[transform,opacity] group-hover/fold:opacity-100 group-data-[state=open]/fold:opacity-100",
              open && "rotate-90"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-0.5">
          <div className="ml-1.5 flex flex-col gap-0.5 border-l border-line-2 pl-2">
            {parts.map((part) => (
              <ToolCard
                key={part.toolCallId}
                part={part}
                onApproval={onApproval}
                onAnswer={onAnswer}
                onConnectResolved={onConnectResolved}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  }
);
ExploredFold.displayName = "ExploredFold";
